import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { payrollAPI } from '../../services/api';
import { useToast } from '../Toast';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';

const PayrollGenerateModal = ({ employeeId, employeeName, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // Default to previous month
  const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);

  const generateMutation = useMutation({
    mutationFn: (data) => payrollAPI.generate(data),
    onSuccess: (response) => {
      const result = response.data;
      if (result.errors && result.errors.length > 0) {
        // Some errors occurred
        const employeeError = result.errors.find(e => e.employee_id === employeeId);
        if (employeeError) {
          showToast(employeeError.error, 'error');
        } else {
          showToast(`Payroll generated for ${result.payrolls_generated} employees`, 'success');
          onSuccess();
        }
      } else {
        showToast(`Payroll generated successfully`, 'success');
        onSuccess();
      }
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to generate payroll', 'error');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    generateMutation.mutate({ month, year });
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [];
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-white">Generate Payroll</h2>
            <p className="text-sm text-gray-400 mt-1">
              This will generate payroll for all employees
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CalendarIcon className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-blue-300 font-medium">Select Payroll Period</p>
                <p className="text-sm text-blue-400/80 mt-1">
                  Choose the month and year for which you want to generate payroll.
                  Salary will be calculated based on attendance records.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Month */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
            <p className="text-yellow-300 text-sm">
              <strong>Note:</strong> Payroll will be generated for all active employees.
              Each employee gets 2 paid leaves per month. Additional leaves will be deducted from salary.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={generateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generateMutation.isPending ? 'Generating...' : 'Generate Payroll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollGenerateModal;
